import { memo, useEffect, useState } from "react";
import { useStore } from "../store/globalState";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useTranslation } from "react-i18next";
import {
  usePreventReload,
  useKeyboardVisible,
} from "lola-framework-ui-test/src/hooks";
import {
  AuraLayout,
  Button,
  type IVGSCardInfo,
  Layout,
  Navbar,
  VgsFormWrapper,
  VgsInput,
} from "lola-framework-ui-test";
import { APP_ROUTES, cardNumberPlaceHolder } from "../constants";
import { config } from "../config";
import {
  CARD_BRANDS,
  DEFAULT_ALLOWED_BRANDS,
  type CardBrandKey,
} from "../constants/card-brands";
import { paymentType } from "../interfaces/payment";
import { NavbarBackChevron } from "../components/NavbarBackChevron";
import { useSessionStore } from "../store/useSessionStore";
import { addCard } from "../services/api";
import { useNavigate } from "react-router-dom";
import { useClaimLink } from "../hooks/useClaimLink";
import { safeCapture } from "../utils/posthog";

const vgsVaultId = config.VGS_COLLECT_VAULT_ID;
const vgsEnvironment = config.VGS_ENV;
const vgsRouteId = config.VGS_COLLECT_ROUTE_ID;

const vgsCardInfoInitialState: IVGSCardInfo = {
  bin: "",
  cardType: "",
  last4: "",
};

export type IVGSFields = {
  card_holder_name?: string;
  card_number?: string;
  card_exp_date?: string;
  card_cvc?: string;
};

export const AddCardCollectPage = memo(() => {
  const {
    cardState,
    setCardState,
    theme,
    setPaymentState,
    cardList,
    setCardList,
  } = useStore();
  useClaimLink();
  const { session } = useSessionStore();
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken();
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState<IVGSFields>({
    card_holder_name: "",
    card_number: "",
    card_exp_date: "",
    card_cvc: "",
  });

  const { isKeyboardOpen, viewportHeight } = useKeyboardVisible();
  const el = document.activeElement as HTMLElement | null;
  const showFooter = el?.tagName === "IFRAME" || isKeyboardOpen;
  const [showInfo, setShowInfo] = useState(showFooter);
  const [vgsCardInfo, setVgsCardInfo] = useState<IVGSCardInfo>(
    vgsCardInfoInitialState
  );

  usePreventReload();

  useEffect(() => {
    let timer = undefined;
    if (!showFooter) {
      timer = setTimeout(() => {
        setShowInfo(showFooter);
      }, 1000);
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowInfo(showFooter);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [showFooter]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormSubmitSuccess = async (status: any, data: any) => {
    if (status === 200 && data) {
      const cardNumber = data.card_number;
      const last4 = vgsCardInfo.last4;
      const brand = vgsCardInfo.cardType.toLocaleUpperCase();
      const name = `${brand}-${last4}`;
      const [expiryMonth, rawExpiryYear] = data.card_exp_date.split('/');
      const expiryYear = rawExpiryYear.trim().length === 2 ? `20${rawExpiryYear.trim()}` : rawExpiryYear.trim();

      try {
        // Sync card with backend
        const linkId = session.data?.linkId;
        if (!linkId) {
          console.error("No linkId found in session");
          return;
        }

        const backendCard = await addCard(linkId, {
          token: cardNumber, // VGS token
          last4,
          brand,
          expiryMonth: expiryMonth.trim(),
          expiryYear: expiryYear.trim(),
          cardholderName: data.card_holder_name,
        });

        // Store card with backend cardId
        const cardData = {
          cardId: backendCard.cardId,
          name,
          brand,
          last4: last4,
          accountNumber: cardNumber,
          expirationDate: data.card_exp_date,
          fullName: data.card_holder_name,
          cvv: data.card_cvc,
        };

        setCardState({
          ...cardState,
          ...cardData,
        });

        setPaymentState({
          payment: {
            cardId: backendCard.cardId,
            type: paymentType.CARD,
            brand: name,
          },
        });

        const cardExists = cardList.find((card) => card.cardId === backendCard.cardId);
        if (!cardExists) setCardList([...cardList, cardData]);

        safeCapture("ux.send.add_card.card.registered", {
          success: true,
          brand,
          last4,
          method: "vgs",
        });

        setErrorMessage({
          card_holder_name: "",
          card_number: "",
          card_exp_date: "",
          card_cvc: "",
        });

        redirectTo(APP_ROUTES.TRANSFER_PAGE);
      } catch (error) {
        console.error("Failed to sync card with backend:", error);
        safeCapture("ux.send.add_card.card.registered", {
          success: false,
          error_code: "CARD_SYNC_FAILED",
          method: "vgs",
        });
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onHandlerError = (error: any) => {
    const messages: IVGSFields = {};
    Object.keys(error).forEach((errorField: string) => {
      const message = error[errorField]?.errorMessages;
      messages[errorField as keyof IVGSFields] = message.join(",\n");
    });
    setErrorMessage(messages);
    safeCapture("ux.send.add_card.form.error", {
      success: false,
      error_code: "VGS_VALIDATION",
      fields: Object.keys(error),
    });
  };
  const colorsConfig = theme?.whiteView;

  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    buttonShowIcon,
    buttonSize,
  } = colorsConfig;

  const allowedBrands = (session.data?.config?.allowedCardBrands ?? DEFAULT_ALLOWED_BRANDS) as CardBrandKey[];
  const isUSOnly = (session.data?.config?.cardOriginPolicy ?? "us_only") === "us_only";

  const brandsLabel = allowedBrands
    .map((key) => CARD_BRANDS[key]?.label)
    .filter(Boolean)
    .join(", ");

  const activeColor = backgroundBtn;
  const inputColor = bodyCopy;

  return (
    <AuraLayout colorConfig={colorsConfig}>
      <Layout.Header>
        <NavbarBackChevron onBackClick={() => navigate(-1)}>
          <Navbar
            align="center"
            color={title}
            title={isUSOnly ? t("card.addUS") : t("card.add")}
            onBackClick={() => navigate(-1)}
          />
        </NavbarBackChevron>
      </Layout.Header>

      <Layout.Content
        className="collect"
        style={{ height: `${viewportHeight}px` }}
      >
        <VgsFormWrapper
          vaultId={vgsVaultId}
          environment={vgsEnvironment as "sandbox" | "live"}
          routeId={vgsRouteId}
          action="/post"
          submitParameters={{}}
          onSubmitCallback={handleFormSubmitSuccess}
          onErrorCallback={onHandlerError}
        >
          <section
            style={{
              display: "flex",
              gap: "8px",
              flexDirection: "column",
            }}
          >
            <VgsInput
              autoFocus
              type="card_holder_name"
              placeholder={t("card.owner")}
              errorLabel={errorMessage.card_holder_name}
              activeColor={activeColor}
              errorColor={inputColor}
              color={inputColor}
            />
            <VgsInput
              type="card_number"
              placeholder={t("card.number")}
              errorLabel={errorMessage.card_number}
              activeColor={activeColor}
              errorColor={inputColor}
              color={inputColor}
              cardNumberFormPlaceholder={cardNumberPlaceHolder}
              onGetCardInfo={setVgsCardInfo}
            />
            <section className="vgs-inline">
              <VgsInput
                type="card_exp_date"
                placeholder={t("card.expiryPlaceholder")}
                errorLabel={errorMessage.card_exp_date}
                activeColor={activeColor}
                errorColor={inputColor}
                color={inputColor}
              />
              <VgsInput
                type="card_cvc"
                placeholder={t("card.cvvPlaceholder")}
                errorLabel={errorMessage.card_cvc}
                activeColor={activeColor}
                errorColor={inputColor}
                color={inputColor}
              />
            </section>
          </section>
          <section className="valid-cards">
            {!showInfo && (
              <section className="valid-cards-container">
                <section className="valid-cards-logos">
                  {allowedBrands.map((key) => {
                    const brand = CARD_BRANDS[key];
                    if (!brand) return null;
                    return (
                      <img
                        key={key}
                        src={brand.logo}
                        alt={t(brand.altKey)}
                      />
                    );
                  })}
                </section>
                <p className="footer-text foreground">
                  {t("card.acceptedBrands", { brands: brandsLabel })}
                </p>
              </section>
            )}
            <section className="homepage-footer">
              <Button
                showIcon={buttonShowIcon ?? true}
                type="submit"
                size={buttonSize ?? "small"}
                color={textColorBtn}
                background={backgroundBtn}
              >
                {t("card.continue")}
              </Button>
            </section>
          </section>
        </VgsFormWrapper>
      </Layout.Content>
    </AuraLayout>
  );
});
